import { useState } from 'react';
import { useCuttingStore } from './state/useCuttingStore';
import { SheetSizeDialog } from './dialogs/SheetSizeDialog';
import type { CuttingSettings, SheetSizeConfig } from './model/types';

type CuttingMethod = CuttingSettings['method'];
type StartPoint = CuttingSettings['startPoint'];

/** Подпись триггера размера листа из конфигурации (project.html:193/1140/1162).
    PR #8a-MOBILE (правка #7): триггер живёт здесь (Настройки, первым блоком),
    перенесён со вкладки «Детали». */
function sheetLabel(cfg: SheetSizeConfig): string {
  if (cfg.mode === 'mixed' && cfg.mixedVariants) {
    const [a, b] = cfg.mixedVariants;
    return `${a.length}×${a.width} / ${b.length}×${b.width}`;
  }
  if (cfg.uniformSheet) return `${cfg.uniformSheet.length} × ${cfg.uniformSheet.width}`;
  return 'не задан';
}

/**
 * Block 14 «Раскрой» — вкладка «Настройки» (PR #5-PARAMS, CUT-2.1).
 *
 * Пиксельный порт `project.html:85-182` (3 секции: Параметры раскроя / Кромки /
 * Метаданные проекта). Контент вкладки внутри `CuttingLayout` (шапка/таб-бар/
 * bottom-bar — снаружи). Заголовок «Настройки» — h1 шапки шелла (CuttingLayout
 * title), отдельный `.sc-section-title` прототипа НЕ дублируем.
 *
 * Связка со стором (in-memory, PR #1/#4): контролируемые инпуты → updateSettings/
 * updateMeta/updateEdges → dirty=true → автосейв PR#4 подхватывает.
 *
 * SVG методов — вербатим прототипа, `stroke #1a1512 → currentColor` (конвенция
 * cuttingTabs.tsx; `--text-primary` = #000, P-039). Текст кнопок методов чёрный
 * (`.sc-method { color: var(--text-primary) }`, решение владельца).
 *
 * `kb-input` сохранён как класс, но это ОБЫЧНЫЙ number input (без `inputmode=none`):
 * кастомная цифровая клавиатура отложена (CUT-2.4, §1 handover). Поля толщины
 * кромки/материала и «Дата создания» — UI-only (контракт ADR-0065 заморожен,
 * P-011): хранятся в local state, в контракт/region-db не персистятся (BACKLOG
 * B-211, довести при EP-3). materialText — персистится (поле контракта).
 */

interface MethodOption {
  id: CuttingMethod;
  label: string;
  rects: Array<{ x: number; y: number; width: number; height: number }>;
}

// Прямоугольники SVG — вербатим project.html:109/113/117.
const METHODS: MethodOption[] = [
  {
    id: 'length',
    label: 'По длине',
    rects: [
      { x: 2, y: 2, width: 46, height: 10 },
      { x: 2, y: 13, width: 46, height: 10 },
      { x: 2, y: 24, width: 46, height: 10 },
    ],
  },
  {
    id: 'width',
    label: 'По ширине',
    rects: [
      { x: 2, y: 2, width: 14, height: 32 },
      { x: 18, y: 2, width: 14, height: 32 },
      { x: 34, y: 2, width: 14, height: 32 },
    ],
  },
  {
    id: 'optimal',
    label: 'Оптимально',
    rects: [
      { x: 2, y: 2, width: 20, height: 14 },
      { x: 24, y: 2, width: 24, height: 14 },
      { x: 2, y: 18, width: 14, height: 16 },
      { x: 18, y: 18, width: 30, height: 16 },
    ],
  },
];

const CORNERS: Array<{ id: StartPoint; title: string }> = [
  { id: 'tl', title: 'Левый верхний' },
  { id: 'tr', title: 'Правый верхний' },
  { id: 'bl', title: 'Левый нижний' },
  { id: 'br', title: 'Правый нижний' },
];

/** '' → undefined; иначе число (для опциональных UI-only толщин). */
function toOptionalNumber(v: string): number | undefined {
  return v === '' ? undefined : Number(v);
}

/** '' → 0; иначе число (для обязательных числовых настроек). */
function toNumber(v: string): number {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

export function ParamsTab() {
  const document = useCuttingStore((s) => s.document);
  const updateSettings = useCuttingStore((s) => s.updateSettings);
  const updateMeta = useCuttingStore((s) => s.updateMeta);
  const updateEdges = useCuttingStore((s) => s.updateEdges);
  const { settings, meta, edges } = document;
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div>
      {/* ── Размер листа (правка #7: первым блоком Настроек, перенесён с «Деталей») ── */}
      <div style={{ padding: '10px 12px' }}>
        <button type="button" className="sheet-size-trigger" onClick={() => setSheetOpen(true)}>
          <span>📐</span>
          <span>
            Размер листа: <strong>{sheetLabel(settings.sheetConfig)}</strong>
          </span>
          <span>▾</span>
        </button>
      </div>

      {/* ── Параметры раскроя ─────────────────────────────────────────── */}
      <div className="sc-settings-section">
        <div className="sc-settings-section-title">Параметры раскроя</div>

        <div className="sc-row">
          <span className="sc-label">Ширина пилы</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Ширина пилы"
            value={settings.kerf}
            onChange={(e) => updateSettings({ kerf: toNumber(e.target.value) })}
          />
          <span className="sc-unit">мм</span>
        </div>

        <div className="sc-row">
          <span className="sc-label">Обпил краев листа</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Обпил краев листа"
            value={settings.edgeOffset}
            onChange={(e) => updateSettings({ edgeOffset: toNumber(e.target.value) })}
          />
          <span className="sc-unit">мм</span>
        </div>

        <div className="sc-row">
          <span className="sc-label">Минимальный размер детали</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Минимальный размер детали"
            value={settings.minPartSize}
            onChange={(e) => updateSettings({ minPartSize: toNumber(e.target.value) })}
          />
          <span className="sc-unit">мм</span>
        </div>

        <div className="sc-row">
          <span
            className="sc-label"
            title="Технологический запас при наклейке кромки — добавляется к расчёту погонажа на вкладке Результат"
          >
            Свес кромки
          </span>
          <input
            type="number"
            id="edge-overhang"
            className="sc-input small kb-input"
            aria-label="Свес кромки"
            min={0}
            max={100}
            value={settings.edgeOverhang}
            onChange={(e) => updateSettings({ edgeOverhang: toNumber(e.target.value) })}
          />
          <span className="sc-unit">мм / сторона</span>
        </div>

        <div className="sc-row" style={{ display: 'block' }}>
          <span className="sc-label" style={{ display: 'block', marginBottom: 8 }}>
            Метод раскроя
          </span>
          <div className="sc-method-choice" style={{ padding: 0 }}>
            {METHODS.map((m) => {
              const active = settings.method === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  className={active ? 'sc-method active' : 'sc-method'}
                  aria-pressed={active}
                  data-method={m.id}
                  onClick={() => updateSettings({ method: m.id })}
                >
                  <svg viewBox="0 0 50 36" aria-hidden="true">
                    {m.rects.map((r, i) => (
                      <rect
                        key={i}
                        x={r.x}
                        y={r.y}
                        width={r.width}
                        height={r.height}
                        fill="none"
                        stroke="currentColor"
                      />
                    ))}
                  </svg>
                  <div className="sc-method-label">{m.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="sc-row" style={{ display: 'block' }}>
          <span className="sc-label" style={{ display: 'block', marginBottom: 8 }}>
            Начальная точка
          </span>
          <div className="sc-start-point">
            {CORNERS.map((c) => {
              const active = settings.startPoint === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  className={active ? `corner ${c.id} active` : `corner ${c.id}`}
                  title={c.title}
                  aria-label={c.title}
                  aria-pressed={active}
                  data-corner={c.id}
                  onClick={() => updateSettings({ startPoint: c.id })}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Кромки ────────────────────────────────────────────────────── */}
      <div className="sc-settings-section">
        <div className="sc-settings-section-title">Кромки</div>

        <div className="sc-row">
          <span className="sc-label">
            Кромка 1 <span className="sc-edge-indicator" />
          </span>
          <input
            type="text"
            className="sc-input medium"
            aria-label="Кромка 1 — название"
            placeholder="ПВХ 2 мм Венге"
            value={edges.edge1.name}
            onChange={(e) => updateEdges({ edge1: { ...edges.edge1, name: e.target.value } })}
          />
          <span className="sc-unit">Толщина:</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Кромка 1 — толщина"
            value={edges.edge1.thickness ?? ''}
            onChange={(e) =>
              updateEdges({
                edge1: { ...edges.edge1, thickness: toOptionalNumber(e.target.value) },
              })
            }
          />
          <span className="sc-unit">мм</span>
        </div>

        <div className="sc-row">
          <span className="sc-label">
            Кромка 2 <span className="sc-edge-indicator type2" />
          </span>
          <input
            type="text"
            className="sc-input medium"
            aria-label="Кромка 2 — название"
            placeholder="меламин 0.4 мм"
            value={edges.edge2.name}
            onChange={(e) => updateEdges({ edge2: { ...edges.edge2, name: e.target.value } })}
          />
          <span className="sc-unit">Толщина:</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Кромка 2 — толщина"
            value={edges.edge2.thickness ?? ''}
            onChange={(e) =>
              updateEdges({
                edge2: { ...edges.edge2, thickness: toOptionalNumber(e.target.value) },
              })
            }
          />
          <span className="sc-unit">мм</span>
        </div>
      </div>

      {/* ── Метаданные проекта ────────────────────────────────────────── */}
      <div className="sc-settings-section">
        <div className="sc-settings-section-title">Метаданные проекта</div>

        <div className="sc-row">
          <span className="sc-label">Материал</span>
          <input
            type="text"
            className="sc-input full"
            aria-label="Материал"
            placeholder="ЛДСП Венге Egger H1115"
            style={{ flex: 1, maxWidth: 320 }}
            value={meta.materialText}
            onChange={(e) => updateMeta({ materialText: e.target.value })}
          />
        </div>

        <div className="sc-row">
          <span className="sc-label">Толщина материала</span>
          <input
            type="number"
            className="sc-input small kb-input"
            aria-label="Толщина материала"
            value={meta.materialThickness ?? ''}
            onChange={(e) => updateMeta({ materialThickness: toOptionalNumber(e.target.value) })}
          />
          <span className="sc-unit">мм</span>
        </div>

        <div className="sc-row">
          <span className="sc-label">Название проекта</span>
          <input
            type="text"
            id="project-name-input"
            className="sc-input medium"
            aria-label="Название проекта"
            placeholder="З-2026-0142"
            value={meta.name}
            onChange={(e) => updateMeta({ name: e.target.value })}
          />
        </div>

        <div className="sc-row">
          <span className="sc-label">Клиент</span>
          <input
            type="text"
            className="sc-input medium"
            aria-label="Клиент"
            placeholder="Иванов А. С."
            value={meta.customerName ?? ''}
            onChange={(e) => updateMeta({ customerName: e.target.value })}
          />
        </div>

        <div className="sc-row">
          <span className="sc-label">Дата</span>
          <input
            type="date"
            className="sc-input medium"
            aria-label="Дата"
            value={meta.createdDate ?? ''}
            onChange={(e) => updateMeta({ createdDate: e.target.value })}
          />
        </div>

        <div className="sc-row">
          <span className="sc-label">Дата готовности</span>
          <input
            type="date"
            className="sc-input medium"
            aria-label="Дата готовности"
            value={meta.dueDate ?? ''}
            onChange={(e) => updateMeta({ dueDate: e.target.value })}
          />
        </div>

        <div className="sc-row" style={{ display: 'block' }}>
          <span className="sc-label" style={{ display: 'block', marginBottom: 8 }}>
            Информация
          </span>
          <textarea
            className="sc-input full"
            aria-label="Информация"
            rows={3}
            placeholder="Любые заметки по проекту"
            value={meta.notes ?? ''}
            onChange={(e) => updateMeta({ notes: e.target.value })}
          />
        </div>
      </div>

      {sheetOpen && <SheetSizeDialog onClose={() => setSheetOpen(false)} />}
    </div>
  );
}
